import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetServiceQuery,
  useUpdateServiceMutation,
  useGetServiceCategoriesQuery,
} from "../redux/services/api";
import { selectIsAuthenticated } from "../redux/slices/authSlice";
import ImageUpload from "../components/ImageUpload";
import MultipleImageUpload from "../components/MultipleImageUpload";
import Icon from "../components/Icon";
import RichTextEditor from "../components/RichTextEditor";
import { useTranslation } from "../hooks/useTranslation";
import StatusMessage from "../components/StatusMessage";
import { useMessageType } from "../hooks/useMessageType";
import { useLanguage } from "../contexts/LanguageContext";

const EditService = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();

  // RTK Query hooks
  const { data: serviceData, isLoading: loading } = useGetServiceQuery(id);
  const [updateService, { isLoading: saving }] = useUpdateServiceMutation();
  const { data: categoriesData, isLoading: loadingCategories } =
    useGetServiceCategoriesQuery();

  const categories = categoriesData?.categories || [];
  const [saveStatus, setSaveStatus] = useState("");
  const messageType = useMessageType(saveStatus);

  const [formData, setFormData] = useState({
    title: "",
    titleAr: "",
    content: "",
    contentAr: "",
    image: null,
    multipleImages: [],
    category_id: "",
    is_active: true,
    keywords: "",
    meta_title: "",
    meta_description: "",
    cta_title: "Need This Service?",
    cta_title_ar: "تحتاج هذه الخدمة؟",
    cta_description: "Contact us today to learn more about this service and how it can benefit your business.",
    cta_description_ar: "اتصل بنا اليوم لمعرفة المزيد عن هذه الخدمة وكيف يمكن أن تفيد عملك.",
    cta_button1_text: "Contact Us Now",
    cta_button1_text_ar: "اتصل بنا الآن",
    cta_button2_text: "View All Services",
    cta_button2_text_ar: "عرض جميع الخدمات",
    cta_button1_link: "/#contact",
    cta_button2_link: "/services",
  });

  const [originalService, setOriginalService] = useState(null);

  // Populate form when service data is loaded
  useEffect(() => {
    if (serviceData && serviceData.service) {
      const service = serviceData.service;
      setOriginalService(service);
      
      // Prepare multiple images array
      const multipleImages = [];
      if (service.multiple_images_urls && Array.isArray(service.multiple_images_urls)) {
        service.multiple_images_urls.forEach((url, index) => {
          multipleImages.push({
            url: url,
            path: service.multiple_images ? service.multiple_images[index] : null,
            isNew: false,
          });
        });
      }
      
      setFormData({
        title: service.title || "",
        titleAr: service.title_ar || "",
        content: service.content || "",
        contentAr: service.content_ar || "",
        image: service.image_url || null,
        multipleImages: multipleImages,
        category_id: service.category_id || "",
        is_active: service.is_active !== false,
        keywords: service.keywords || "",
        meta_title: service.meta_title || "",
        meta_description: service.meta_description || "",
        cta_title: service.cta_title || "Need This Service?",
        cta_title_ar: service.cta_title_ar || "تحتاج هذه الخدمة؟",
        cta_description: service.cta_description || "Contact us today to learn more about this service and how it can benefit your business.",
        cta_description_ar: service.cta_description_ar || "اتصل بنا اليوم لمعرفة المزيد عن هذه الخدمة وكيف يمكن أن تفيد عملك.",
        cta_button1_text: service.cta_button1_text || "Contact Us Now",
        cta_button1_text_ar: service.cta_button1_text_ar || "اتصل بنا الآن",
        cta_button2_text: service.cta_button2_text || "View All Services",
        cta_button2_text_ar: service.cta_button2_text_ar || "عرض جميع الخدمات",
        cta_button1_link: service.cta_button1_link || "/#contact",
        cta_button2_link: service.cta_button2_link || "/services",
      });
    }
  }, [serviceData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      setSaveStatus(t("addEditService.pleaseLoginToSave"));
      return;
    }

    // Validate form
    if (!formData.title.trim()) {
      setSaveStatus(t("addEditService.pleaseEnterTitle"));
      return;
    }

    if (!formData.content.trim()) {
      setSaveStatus(t("addEditService.pleaseEnterContent"));
      return;
    }

    if (!formData.category_id) {
      setSaveStatus(t("addEditService.pleaseSelectCategory"));
      return;
    }

    setSaveStatus("");

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("titleAr", formData.titleAr);
      submitData.append("content", formData.content);
      submitData.append("contentAr", formData.contentAr);
      submitData.append("category_id", formData.category_id);
      submitData.append("is_active", formData.is_active ? "1" : "0");
      submitData.append("keywords", formData.keywords || "");
      submitData.append("meta_title", formData.meta_title || "");
      submitData.append("meta_description", formData.meta_description || "");
      submitData.append("cta_title", formData.cta_title || "");
      submitData.append("cta_title_ar", formData.cta_title_ar || "");
      submitData.append("cta_description", formData.cta_description || "");
      submitData.append("cta_description_ar", formData.cta_description_ar || "");
      submitData.append("cta_button1_text", formData.cta_button1_text || "");
      submitData.append("cta_button1_text_ar", formData.cta_button1_text_ar || "");
      submitData.append("cta_button2_text", formData.cta_button2_text || "");
      submitData.append("cta_button2_text_ar", formData.cta_button2_text_ar || "");
      submitData.append("cta_button1_link", formData.cta_button1_link || "");
      submitData.append("cta_button2_link", formData.cta_button2_link || "");

      // Handle main image removal
      // Check if original service had an image
      const hadImage = originalService && originalService.image_url;
      // Check if current form has an image (either new file or existing URL)
      const hasImage = formData.image && (
        typeof formData.image === "object" || 
        (typeof formData.image === "string" && formData.image.trim() !== "")
      );
      
      if (hadImage && !hasImage) {
        // Original had image but now it's removed
        submitData.append("remove_image", "1");
      } else if (formData.image && typeof formData.image === "object") {
      // Only append image if it's a new file (not existing URL)
        submitData.append("image", formData.image);
      }

      // Handle multiple images
      const imagesToRemove = [];
      const newImages = [];
      
      if (Array.isArray(formData.multipleImages)) {
        formData.multipleImages.forEach((img) => {
          if (img.removed && img.path) {
            imagesToRemove.push(img.path);
          } else if (img.isNew && img.file) {
            newImages.push(img.file);
          }
        });
      }

      // Append images to remove
      if (imagesToRemove.length > 0) {
        imagesToRemove.forEach((path) => {
          submitData.append("remove_images[]", path);
        });
      }

      // Append new images
      newImages.forEach((file) => {
        submitData.append("images[]", file);
      });

      const result = await updateService({ id, body: submitData }).unwrap();

      if (result.success) {
        setSaveStatus(t("addEditService.serviceUpdatedSuccessfully"));
        setTimeout(() => {
          navigate(getLocalizedPath("/dashboard/services"));
        }, 1500);
      } else {
        setSaveStatus(t("addEditService.errorUpdatingService"));
      }
    } catch (error) {
      console.error("Error updating service:", error);
      setSaveStatus(
        error.data?.message || t("addEditService.errorUpdatingService")
      );
    }
  };

  const handleCancel = () => {
    navigate(getLocalizedPath("/dashboard/services"));
  };

  if (loading) {
    return (
      <div className="add-service">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t("addEditService.loadingService")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="add-service">
        <div className="auth-required">
          <h2>{t("addEditService.authenticationRequired")}</h2>
          <p>{t("addEditService.pleaseLoginToEdit")}</p>
          <button
            className="login-btn"
            onClick={() => navigate(getLocalizedPath("/login"))}
          >
            {t("addEditService.goToLogin")}
          </button>
        </div>
      </div>
    );
  }

  if (!originalService) {
    return (
      <div className="add-service">
        <div className="auth-required">
          <h2>{t("addEditService.serviceNotFound")}</h2>
          <p>{t("addEditService.serviceNotFoundMessage")}</p>
          <button
            className="login-btn"
            onClick={() => navigate(getLocalizedPath("/dashboard/services"))}
          >
            {t("addEditService.backToServices")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="add-service">
      <div className="page-header">
        <div className="header-content">
          <button type="button" className="back-btn" onClick={handleCancel}>
            <Icon name="arrow-left" size="16px" />
            {t("addEditService.backToServices")}
          </button>
          <h1>{t("addEditService.editService")}</h1>
          <p>{t("addEditService.updateServiceDetails")}</p>
        </div>
      </div>

      <div className="form-container">
        <div className="form-card">
          <div className="form-content">
            <div className="form-group">
              <label>{t("addEditService.serviceTitleEnglish")} *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder={t("addEditService.enterServiceTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceTitleArabic")}</label>
              <input
                type="text"
                value={formData.titleAr}
                onChange={(e) => handleInputChange("titleAr", e.target.value)}
                placeholder={t("addEditService.enterServiceTitleArabic")}
                className="form-input"
                dir="rtl"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceCategory")} *</label>
              <select
                value={formData.category_id}
                onChange={(e) =>
                  handleInputChange("category_id", e.target.value)
                }
                className="form-input"
                disabled={loadingCategories}
              >
                <option value="">
                  {loadingCategories
                    ? t("servicesDashboard.loading")
                    : t("addEditService.selectCategory")}
                </option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <ImageUpload
                label={t("addEditService.serviceImage")}
                value={formData.image}
                onChange={(file) => handleInputChange("image", file)}
                className="image-upload-modern"
              />
            </div>

            <div className="form-group">
              <MultipleImageUpload
                label={t("addEditService.multipleImages") || "Additional Images"}
                value={formData.multipleImages}
                onChange={(images) => handleInputChange("multipleImages", images)}
                className="image-upload-modern"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceContentEnglish")} *</label>
              <RichTextEditor
                value={formData.content}
                onChange={(content) => handleInputChange("content", content)}
                placeholder={t("addEditService.enterServiceDetails")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.serviceContentArabic")}</label>
              <RichTextEditor
                value={formData.contentAr}
                onChange={(content) => handleInputChange("contentAr", content)}
                placeholder={t("addEditService.enterServiceDetailsArabic")}
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.keywords")}</label>
              <textarea
                value={formData.keywords}
                onChange={(e) => handleInputChange("keywords", e.target.value)}
                placeholder={t("addEditService.enterKeywords")}
                className="form-input"
                rows="3"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditService.keywordsHint")}
              </small>
            </div>

            <div className="form-group">
              <label>{t("addEditService.metaTitle")}</label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) => handleInputChange("meta_title", e.target.value)}
                placeholder={t("addEditService.enterMetaTitle")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditService.metaTitleHint")}
              </small>
            </div>

            <div className="form-group">
              <label>{t("addEditService.metaDescription")}</label>
              <textarea
                value={formData.meta_description}
                onChange={(e) => handleInputChange("meta_description", e.target.value)}
                placeholder={t("addEditService.enterMetaDescription")}
                className="form-input"
                rows="3"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditService.metaDescriptionHint")}
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) =>
                    handleInputChange("is_active", e.target.checked)
                  }
                />
                <span>{t("addEditService.activeService")}</span>
              </label>
            </div>

            {/* CTA Section */}
            <div className="form-section-divider">
              <h3>{t("addEditService.ctaSection")}</h3>
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaTitle")}</label>
              <input
                type="text"
                value={formData.cta_title}
                onChange={(e) => handleInputChange("cta_title", e.target.value)}
                placeholder={t("addEditService.enterCtaTitle")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaTitleAr")}</label>
              <input
                type="text"
                value={formData.cta_title_ar}
                onChange={(e) => handleInputChange("cta_title_ar", e.target.value)}
                placeholder={t("addEditService.enterCtaTitleAr")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaDescription")}</label>
              <textarea
                value={formData.cta_description}
                onChange={(e) => handleInputChange("cta_description", e.target.value)}
                placeholder={t("addEditService.enterCtaDescription")}
                className="form-input"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaDescriptionAr")}</label>
              <textarea
                value={formData.cta_description_ar}
                onChange={(e) => handleInputChange("cta_description_ar", e.target.value)}
                placeholder={t("addEditService.enterCtaDescriptionAr")}
                className="form-input"
                rows="3"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaButton1Text")}</label>
              <input
                type="text"
                value={formData.cta_button1_text}
                onChange={(e) => handleInputChange("cta_button1_text", e.target.value)}
                placeholder={t("addEditService.enterCtaButton1Text")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaButton1TextAr")}</label>
              <input
                type="text"
                value={formData.cta_button1_text_ar}
                onChange={(e) => handleInputChange("cta_button1_text_ar", e.target.value)}
                placeholder={t("addEditService.enterCtaButton1TextAr")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaButton2Text")}</label>
              <input
                type="text"
                value={formData.cta_button2_text}
                onChange={(e) => handleInputChange("cta_button2_text", e.target.value)}
                placeholder={t("addEditService.enterCtaButton2Text")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaButton2TextAr")}</label>
              <input
                type="text"
                value={formData.cta_button2_text_ar}
                onChange={(e) => handleInputChange("cta_button2_text_ar", e.target.value)}
                placeholder={t("addEditService.enterCtaButton2TextAr")}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaButton1Link")}</label>
              <input
                type="text"
                value={formData.cta_button1_link}
                onChange={(e) => handleInputChange("cta_button1_link", e.target.value)}
                placeholder={t("addEditService.enterCtaButton1Link")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditService.ctaButtonLinkHint")}
              </small>
            </div>

            <div className="form-group">
              <label>{t("addEditService.ctaButton2Link")}</label>
              <input
                type="text"
                value={formData.cta_button2_link}
                onChange={(e) => handleInputChange("cta_button2_link", e.target.value)}
                placeholder={t("addEditService.enterCtaButton2Link")}
                className="form-input"
              />
              <small style={{ color: "#999", marginTop: "5px", display: "block" }}>
                {t("addEditService.ctaButtonLinkHint")}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="save-section">
        <StatusMessage
          message={saveStatus}
          type={messageType}
          show={!!saveStatus}
          autoClose={messageType === "success"}
          autoCloseDelay={3000}
          onClose={() => setSaveStatus("")}
        />
        <div className="save-actions">
          <button
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
            disabled={saving}
          >
            {t("addEditService.cancel")}
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="loading-spinner small"></div>
                {t("addEditService.updating")}
              </>
            ) : (
              <>
                <Icon name="save" size="16px" />
                {t("addEditService.updateService")}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditService;
